// services/zendeskService.js
import apiClient, { withAuth } from './api';

export const zendeskService = {
  /**
   * Create a support ticket
   * @param {Object} ticketData - The ticket data containing subject, description, etc.
   * @param {string} token - Auth token
   * @returns {Promise} - Response with ticket information
   */
  createTicket: (ticketData, token) => {
    console.log('Creating support ticket:', ticketData);
    return apiClient.post('/api/support/create-ticket/', ticketData, withAuth(token))
      .then(response => {
        console.log('Ticket created successfully:', response.data);
        return response;
      })
      .catch(error => {
        console.error('Error creating support ticket:', error);
        throw error;
      });
  },

  /**
   * Upload attachments to a ticket
   * @param {number} ticketId - The ticket ID
   * @param {FormData} formData - FormData with files
   * @param {string} token - Auth token
   * @returns {Promise} - Response confirmation
   */
  uploadAttachments: (ticketId, formData, token) => {
    console.log(`Uploading attachments to ticket ${ticketId}`);
    const config = {
      ...withAuth(token),
      headers: {
        ...withAuth(token).headers,
        'Content-Type': 'multipart/form-data',
      }
    };
    
    return apiClient.post(`/api/support/upload-attachment/${ticketId}/`, formData, config)
      .then(response => {
        console.log('Attachments uploaded successfully:', response.data);
        return response;
      })
      .catch(error => {
        console.error('Error uploading attachments:', error);
        throw error;
      });
  },

  /**
   * Get ticket status
   * @param {number} ticketId - The ticket ID
   * @param {string} token - Auth token
   * @returns {Promise} - Response with ticket status
   */
  getTicketStatus: (ticketId, token) => {
    console.log(`Fetching status for ticket ${ticketId}`);
    return apiClient.get(`/api/support/ticket/${ticketId}/`, withAuth(token))
      .then(response => {
        console.log('Ticket status retrieved:', response.data);
        return response;
      })
      .catch(error => {
        console.error('Error fetching ticket status:', error);
        throw error;
      });
  },

  /**
   * Get all tickets for the authenticated user
   * @param {Object} params - Query parameters (page, per_page, status)
   * @param {string} token - Auth token
   * @returns {Promise} - Response with user's tickets
   */
  getUserTickets: (params, token) => {
    console.log('Fetching user tickets with params:', params);
    const queryString = new URLSearchParams(params).toString();
    
    return apiClient.get(`/api/support/my-tickets/?${queryString}`, withAuth(token))
      .then(response => {
        console.log('User tickets retrieved:', response.data);
        return response;
      })
      .catch(error => {
        console.error('Error fetching user tickets:', error);
        throw error;
      });
  },

  /**
   * Search the knowledge base
   * @param {Object} params - Search parameters (query, locale, per_page)
   * @param {string} token - Auth token
   * @returns {Promise} - Response with search results
   */
  searchKnowledgeBase: (params, token) => {
    console.log('Searching knowledge base:', params);
    const queryString = new URLSearchParams(params).toString();
    
    return apiClient.get(`/api/support/search-kb/?${queryString}`, withAuth(token))
      .then(response => {
        console.log('Knowledge base search results:', response.data);
        return response;
      })
      .catch(error => {
        console.error('Error searching knowledge base:', error);
        throw error;
      });
  },

  /**
   * Get real-time updates for a ticket
   * @param {number} ticketId - The ticket ID
   * @param {string} token - Auth token
   * @returns {Promise} - Response with ticket updates
   */
  getTicketUpdates: (ticketId, token) => {
    console.log(`Fetching real-time updates for ticket ${ticketId}`);
    
    return apiClient.get(`/api/support/ticket/${ticketId}/updates/`, withAuth(token))
      .then(response => {
        console.log('Ticket updates retrieved:', response.data);
        return response;
      })
      .catch(error => {
        console.error('Error fetching ticket updates:', error);
        throw error;
      });
  },

  /**
   * Subscribe to ticket updates via WebSocket (optional future enhancement)
   * @param {number} ticketId - The ticket ID
   * @param {function} onUpdate - Callback function for updates
   * @param {string} token - Auth token
   * @returns {WebSocket} - WebSocket connection
   */
  subscribeToTicketUpdates: (ticketId, onUpdate, token) => {
    const wsUrl = process.env.REACT_APP_WS_URL || 'wss://api.circledin.com';
    const ws = new WebSocket(`${wsUrl}/ws/support/ticket/${ticketId}/?token=${token}`);
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('Received ticket update:', data);
      if (onUpdate) {
        onUpdate(data);
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    ws.onclose = () => {
      console.log('WebSocket connection closed');
    };
    
    return ws;
  }
};

export default zendeskService;